// La simulation : faire avancer le temps, en jeu comme pendant l'absence du joueur.
import type { GameState } from '../engine/state';
import {
  AUTO_OFFLINE_CAP_DAYS,
  DAYS_PER_YEAR,
  FAST_FORWARD_SPEED,
  OFFLINE_THRESHOLD_MS,
  PROLOGUE_SPEED,
  REAL_SECONDS_PER_DAY,
  RESERVE_CAP_DAYS,
  dateAtDay,
  monthIndexAt,
} from '../engine/time';
import { accrueOverdraftInterest, rearmLowEnergy, unlockPage } from './actions';
import { checkPromotion, dailyCareer } from './career';
import { inPrivateSchool, networth, processMonth, recordIncome } from './economy';
import { accrueAvInterest, hasMarketShares, recordAvHistory } from './av';
import { accrueSavingsInterest } from './livret';
import { advanceMarket } from './market';
import { applyMarketMonth, recordPeaHistory } from './pea';
import { matureTermDeposits } from './termDeposit';
import { eventDue, maybeTriggerEvent, startDueFollowUp, startEvent } from './events';
import { checkObjectives, checkPaliers, startFirstJob } from './progress';
import { annualReview } from './review';
import { maybeCashTemptation } from './temptations';
import { wearUpgrades } from './upgrades';
import { applyLowEnergyMalus, maybeMoodEvent } from './wellbeing';
import { energyMax, regenEnergy } from './work';

/** La première baisse terminée (ou tombée pendant un krach), une carte fait le point si l'on avait des parts (PEA ou assurance-vie). */
function maybeFirstDropCard(state: GameState): void {
  const market = state.market;
  if (!market.firstDropDone || market.regime === 'correction' || state.flags.firstDropCard || state.pendingEvent) return;
  state.flags.firstDropCard = true;
  if (hasMarketShares(state)) startEvent(state, 'premiere_baisse');
}

/** La première rentrée : la réponse de la bourse sur critères sociaux, une seule fois, avant le prêt. */
export function maybeScholarshipCard(state: GameState): void {
  if (state.pendingEvent || state.eventCounts.bourse_etudes || !inPrivateSchool(state)) return;
  if (state.milieu === 'aise') return;
  if (dateAtDay(state.startDate, state.day).getMonth() !== 8) return;
  startEvent(state, 'bourse_etudes');
}

/** La première rentrée de l'école privée : en septembre, la carte du prêt étudiant, une seule fois. */
export function maybeStudentLoanCard(state: GameState): void {
  if (state.pendingEvent || state.eventCounts.pret_etudiant || !inPrivateSchool(state)) return;
  if (dateAtDay(state.startDate, state.day).getMonth() !== 8) return;
  startEvent(state, 'pret_etudiant');
}

/** Le temps de la vie peut-il avancer (hors fenêtres qui mettent en pause) ? */
export function lifeCanAdvance(state: GameState): boolean {
  if (state.pendingEvent) return false;
  if (state.stage === 'prologue') return state.prologueStep === 'years';
  return true;
}

/** Le temps avance-t-il à l'écran en ce moment ? */
export function isRunning(state: GameState): boolean {
  if (!lifeCanAdvance(state)) return false;
  return !(
    state.offlineSummary ||
    state.stageIntro ||
    state.pendingReview ||
    state.unreadPages.length > 0
  );
}

export function canFastForward(state: GameState): boolean {
  return state.stage !== 'prologue' && state.reserveDays > 0;
}

export function setFastForward(state: GameState, on: boolean): void {
  state.speed.fastForward = on && canFastForward(state);
}

/** Un battement du métronome (toutes les 250 ms). */
export function tick(state: GameState, now: number): void {
  const elapsed = now - state.lastRealTime;
  if (elapsed > OFFLINE_THRESHOLD_MS) {
    applyOffline(state, now);
    return;
  }
  const realMs = Math.max(0, elapsed);
  state.lastRealTime = now;
  regenEnergy(state, realMs);
  rearmLowEnergy(state);
  // Les objectifs se vérifient à chaque battement : un achat ou une économie se voit tout de suite.
  checkObjectives(state);
  if (!isRunning(state)) return;
  // Être à plat pèse sur le moral, mais seulement quand le temps passe.
  applyLowEnergyMalus(state, realMs);

  let speed = state.speed.dev * (state.stage === 'prologue' ? PROLOGUE_SPEED : 1);
  const fastForward = state.speed.fastForward && canFastForward(state);
  if (fastForward) speed *= FAST_FORWARD_SPEED;
  const days = (realMs / 1000 / REAL_SECONDS_PER_DAY) * speed;
  if (fastForward) {
    // L'avance rapide consomme la réserve : trois jours sur quatre viennent d'elle.
    state.reserveDays = Math.max(0, state.reserveDays - (days * (FAST_FORWARD_SPEED - 1)) / FAST_FORWARD_SPEED);
    if (state.reserveDays === 0) state.speed.fastForward = false;
  }
  advanceDays(state, days, false);
}

/**
 * Fait avancer la vie d'un certain nombre de jours, jour par jour.
 * Renvoie les jours qui n'ont pas pu être joués (une décision attend le joueur).
 */
export function advanceDays(state: GameState, days: number, offline: boolean): number {
  let remaining = days;
  while (remaining > 1e-9) {
    if (offline ? !lifeCanAdvance(state) : !isRunning(state)) break;
    const step = Math.min(1, remaining);
    remaining -= step;
    state.day += step;

    dailyCareer(state, step);
    accrueSavingsInterest(state, step);
    accrueAvInterest(state, step);
    // La bourse avance jour après jour ; ses nouvelles du mois arrivent au PEA.
    for (const month of advanceMarket(state)) applyMarketMonth(state, month);
    recordPeaHistory(state);
    recordAvHistory(state);
    if (!offline) {
      maybeFirstDropCard(state);
      maybeScholarshipCard(state);
      maybeStudentLoanCard(state);
    }
    matureTermDeposits(state);
    accrueOverdraftInterest(state, step);
    wearUpgrades(state);
    if (state.stage === 'prologue' && state.firstJobTargetDay !== null && state.day >= state.firstJobTargetDay) {
      startFirstJob(state);
    }

    const monthIndex = monthIndexAt(state.startDate, state.day);
    while (state.lastProcessedMonth < monthIndex) {
      state.lastProcessedMonth += 1;
      monthlyStep(state, offline);
    }

    if (state.firstJobDay !== null) {
      const years = Math.floor((state.day - state.firstJobDay) / DAYS_PER_YEAR);
      if (years > state.yearsReviewed) annualReview(state);
    }

    // Une suite programmée (« on en reparle dans deux jours ») arrive à son jour.
    if (!offline) startDueFollowUp(state);

    const week = Math.floor(state.day / 7);
    if (week > state.lastEventWeek) {
      state.lastEventWeek = week;
      // Une carte quand c'est le moment : d'abord le moral (coup de blues, épuisement), puis les envies
      // nées de l'argent qui dort sur le compte courant, puis les autres événements.
      if (!offline && eventDue(state) && !maybeMoodEvent(state) && !maybeCashTemptation(state)) maybeTriggerEvent(state);
    }
  }
  return remaining;
}

function monthlyStep(state: GameState, offline: boolean): void {
  const report = processMonth(state);
  if (report.interest > 0) unlockPage(state, 'interet_compose');
  if (state.stage !== 'prologue') {
    recordIncome(state, report.income);
    state.year.saved += report.saved;
    if (!report.negative) state.flags.monthWithoutOverdraft = true;
  }
  checkPromotion(state);
  checkPaliers(state);
  checkObjectives(state);
  if (
    !offline &&
    state.stage === 'prologue' &&
    state.path === 'alternance' &&
    !state.flags.firstAlternancePay &&
    !state.pendingEvent
  ) {
    state.flags.firstAlternancePay = true;
    startEvent(state, 'premiere_paie_alternance');
  }
}

/**
 * Le retour après une absence : pour l'instant, la vie s'est mise en pause et reprend où elle en était.
 * Seule l'énergie est revenue, comme pendant une pause en jeu.
 */
export function applyOffline(state: GameState, now: number): void {
  const absentMs = Math.max(0, now - state.lastRealTime);
  state.lastRealTime = now;
  regenEnergy(state, absentMs);
}

/**
 * Le « mélange » du document de conception, mis de côté pour l'instant : au retour, jusqu'à un an
 * est calculé automatiquement, le reste va dans la réserve de temps de l'avance rapide.
 * Pour le remettre, applyOffline l'appelle avec la durée de l'absence.
 */
export function catchUpOffline(state: GameState, absentMs: number): void {
  if (state.energyUnlocked) state.energy = energyMax(state);
  const inPrologueScreens = state.stage === 'prologue' && state.prologueStep !== 'years';
  if (inPrologueScreens || absentMs < OFFLINE_THRESHOLD_MS) return;

  const absentDays = absentMs / 1000 / REAL_SECONDS_PER_DAY;
  const networthBefore = networth(state);
  const interestBefore = state.totalInterest;
  const monthBefore = state.lastProcessedMonth;

  let playedDays = 0;
  let unplayed = absentDays;
  if (lifeCanAdvance(state)) {
    const autoDays = Math.min(absentDays, AUTO_OFFLINE_CAP_DAYS);
    const leftover = advanceDays(state, autoDays, true);
    playedDays = autoDays - leftover;
    unplayed = absentDays - playedDays;
  }

  // Le prologue ne remplit pas la réserve : elle sert à l'avance rapide de la vie adulte.
  const reserveAdded =
    state.stage === 'prologue' ? 0 : Math.min(unplayed, RESERVE_CAP_DAYS - state.reserveDays);
  state.reserveDays += Math.max(0, reserveAdded);

  state.offlineSummary = {
    months: state.lastProcessedMonth - monthBefore,
    networthBefore,
    networthAfter: networth(state),
    interest: state.totalInterest - interestBefore,
    reserveAddedDays: Math.max(0, reserveAdded),
  };
}
